import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface DropdownMenuItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Draw a separator above this item. */
  dividerBefore?: boolean;
  /** Marks the current choice (e.g. the event's status). */
  selected?: boolean;
}

export interface DropdownMenuProps {
  /** Renders the trigger; spread `props` onto a `<button>`. */
  trigger: (props: {
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
    onClick: () => void;
  }) => ReactNode;
  items: DropdownMenuItem[];
  /** Which edge of the trigger the menu lines up with. */
  align?: 'start' | 'end';
  ariaLabel?: string;
  menuClassName?: string;
}

/**
 * A small popover menu of actions: closes on outside click, Escape or a
 * choice, and moves focus with the arrow keys. Used for the event header's
 * "More" menu, the clickable status and the tab bar's overflow.
 */
export function DropdownMenu({
  trigger,
  items,
  align = 'start',
  ariaLabel,
  menuClassName = 'w-56',
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointer);
    const first = itemRefs.current.find((el) => el && !el.disabled);
    first?.focus();
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open, close]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const enabled = itemRefs.current.filter((el): el is HTMLButtonElement => !!el && !el.disabled);
    if (enabled.length === 0) return;
    const at = enabled.indexOf(document.activeElement as HTMLButtonElement);
    const step = e.key === 'ArrowDown' ? 1 : -1;
    enabled[(at + step + enabled.length) % enabled.length].focus();
  };

  return (
    <div ref={containerRef} className="relative inline-flex" onKeyDown={onKeyDown}>
      {trigger({
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': menuId,
        onClick: () => setOpen((o) => !o),
      })}
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={ariaLabel}
          className={`absolute top-full mt-1.5 z-40 bg-white border border-gray-200 rounded-xl shadow-lg p-1.5 animate-menu-in ${
            align === 'end' ? 'right-0' : 'left-0'
          } ${menuClassName}`}
        >
          {items.map((item, i) => (
            <div key={item.id}>
              {item.dividerBefore && <div className="my-1 h-px bg-gray-100" role="separator" />}
              <button
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                aria-current={item.selected || undefined}
                disabled={item.disabled}
                onClick={() => {
                  close();
                  item.onSelect();
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors focus:outline-none focus:bg-primary-50 hover:bg-primary-50 disabled:opacity-40 disabled:cursor-not-allowed ${
                  item.selected ? 'font-semibold text-primary-700' : 'text-gray-700'
                }`}
              >
                {item.icon && <span className="text-gray-400 flex-shrink-0">{item.icon}</span>}
                <span className="flex-1 min-w-0">{item.label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
